//  Component: ManageAuthor
//  Purpose: Manages Author Profile creation, editing, and data management within a content management system (CMS).
//  Key Features:
//  Handles CRUD (Create, Read, Update, Delete) operations for Author Profile.
//  Dispatches Redux actions to interact with backend services.
//  Manages Author Profile data efficiently, including Author's latest writings and basic details. 

import React, { useState, useEffect } from "react";
import { getAuthorData, clearAuthorDataResponse, postAuthorProfile, clearPostAuthorProfileResponse, clearPutAuthorProfileResponse, putAuthorProfile } from "../../../../store/slices/authorProfile"
import { useParams, useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import ManageAuthorForm from "./manageAuthorForm"
import { useDispatch, useSelector } from "react-redux";
import {
    AUTHOR_ENTITY_TYPE,
} from "../../../constants/cms";

export const LATEST_WRITINGS = {
    entity_type: "",
    entity_search: "",
    order: "",
};

const ManageAuthor = (props) => {
    const { id } = useParams();
    let navigate = useNavigate();
    const [newValues, setNewValues] = useState({})
    const dispatch = useDispatch();
    const { authorData, entitySearchListData, locationData, locationDataLoading, userData, postAuthorProfileData, postAuthorProfileError, putAuthorProfileData, putAuthorProfileError } = useSelector((state) => state.authorReducer);
    const { user, userPermission } = useSelector((state) => state.userData);
    // Initial values for the form
    const initialValues = {
        designation: "",
        experience: "",
        education: "",
        author_location: "",
        linkedin: "",
        author_about: "",
        latest_writings: [LATEST_WRITINGS]
    }

    useEffect(() => {
        if (id) {
            dispatch(getAuthorData(id))
        }
        return () => {
            dispatch(clearAuthorDataResponse())
        };
    }, [id])

    // Setting up Form state in Edit Case
    useEffect(() => {
        if (authorData) {
            let modifiedValues = Object.assign({}, authorData?.author_profile_data);
            let formattedData = []
            for (let i = 0; i < authorData?.author_popular_content?.length; i++) {
                let obj = {}
                let element = authorData?.author_popular_content[i]
                let entity_type = AUTHOR_ENTITY_TYPE?.find((object) => object.entity_type == element?.entity_type)
                obj.entity_type = entity_type
                obj.order = element?.order
                obj.entity_search = element.entity_data
                formattedData.push(obj)
            }
            modifiedValues.latest_writings = formattedData?.length > 0 ? formattedData : [LATEST_WRITINGS]
            modifiedValues.content_autofetched = authorData?.author_profile_data?.content_autofetched == 1 ? true : false
            modifiedValues.author_type = getAuthorType(authorData?.author_profile_data?.author_type)
            setNewValues(modifiedValues)
        }
    }, [authorData])

    // ------------ Handling Navigation after submission in case of Save and Edit ----------------
    useEffect(() => {
        if (
            postAuthorProfileData
        ) {
            setTimeout(() => {
                dispatch(clearPostAuthorProfileResponse())
                navigate(`/cms/author`);
            }, 1000);
        }
        if (
            postAuthorProfileError
        ) {
            setTimeout(() => {
                dispatch(clearPostAuthorProfileResponse())
            }, 2000);
        }
    }, [
        (postAuthorProfileData || postAuthorProfileError)
    ]);

    useEffect(() => {
        if (
            putAuthorProfileData
        ) {
            setTimeout(() => {
                dispatch(clearPutAuthorProfileResponse())
                navigate(`/cms/author`);
            }, 1000);
        }
        if (
            putAuthorProfileError
        ) {
            setTimeout(() => {
                dispatch(clearPutAuthorProfileResponse())
            }, 2000);
        }
    }, [
        (putAuthorProfileData || putAuthorProfileError)
    ]);

    // ----------- /////////////////////////////////////////// ------------------

    const getAuthorType = (authorType) => {
        if (authorType == "Article") {
            return { article: true };
        } else if (authorType == "News") {
            return { news: true };
        } else if (authorType == "All") {
            return { article: true, news: true };
        }
    };

    const submit = (values) => {
        const params = new FormData();
        params.append("user", parseInt(values.user));
        params.append("designation", values.designation);
        params.append("linkedin", values.linkedin ?? "");
        if (values.profile_image && !(typeof values.profile_image == "string")) {
            params.append("profile_image", values.profile_image);
        }
        params.append("twitter", values.twitter ?? "");
        params.append("instagram", values.instagram ?? "");
        params.append("facebook", values.facebook ?? "");
        params.append("experience", values.experience);
        params.append("education", values.education);
        params.append("author_username", values.author_username);
        params.append("author_name", values.author_name);
        params.append("author_email", values.author_email);
        params.append("author_mobile", values.author_mobile);
        params.append("author_about", values.author_about);
        params.append("author_location", values.author_location?.id);
        params.append("author_active", values.author_active);
        params.append("status", values.status);
        params.append("content_autofetched", values.content_autofetched ? 1 : 0)
        const latestWritingsFormattedData = values.latest_writings?.map((item) => ({
            author: parseInt(id),
            entity_type: item.entity_type?.entity_type,
            order: item.order ? parseInt(item.order) : 1,
            entity_id: parseInt(item.entity_search?.id)
        }));

        // author_popular_content is getting empty keys so if entity search is not selected then passing author_popular_content as empty []
        params.append("author_popular_content", latestWritingsFormattedData?.length > 0 && !latestWritingsFormattedData[0]?.entity_id ? "[]" : JSON.stringify(latestWritingsFormattedData));

        const getTrueKeys = (obj) => {
            if (!obj || typeof obj !== 'object') {
                return [];
            }
            return Object.keys(obj).filter(key => obj[key] === true);
        };

        const trueKeys = getTrueKeys(values.author_type);

        if (trueKeys.length > 0) {
            const authorType = trueKeys.length === 1 ? trueKeys[0] : "All";
            params.append("author_type", authorType);
        } else {
            params.append("author_type", null);
        }
        if (values.domain)
            params.append("domain", parseInt(values.domain))

        if (id) {
            dispatch(putAuthorProfile(id, params));
        } else {
            let data = {}
            data.author_id = values.author_username?.author_id
            data.user = user.data?.id
            dispatch(postAuthorProfile(data));
        }
    };
    return (
        <>
            {/* In Save Case: Notification for Success and Error */}
            <div className={"notification_popup"}>
                {(postAuthorProfileData || postAuthorProfileError) &&
                    <Alert severity={postAuthorProfileError ? `error` : `success`}>
                        {" "}
                        {postAuthorProfileError ? postAuthorProfileError : "Author Profile Added Successfully"}{" "}
                    </Alert>
                }
            </div>
            {/* In Edit Case: Notification for Success and Error */}
            <div className={"notification_popup"}>
                {(putAuthorProfileData || putAuthorProfileError) && (
                    <Alert severity={putAuthorProfileError ? `error` : `success`}>
                        {" "}
                        {putAuthorProfileError ? "Failure: Author Profile not Created" : "Author Profile Added Successfully"}{" "}
                    </Alert>
                )}
            </div>
            {/* Rendering From In Edit Case */}
            {id && newValues && (
                <ManageAuthorForm
                    initialValues={newValues}
                    submit={(values) => submit(values)}
                    id={id}
                    entitySearchListData={entitySearchListData}
                    locationData={locationData}
                    locationDataLoading={locationDataLoading}
                    userData={userData}
                    userPermission={userPermission}
                    authorData={authorData}
                />
            )}
            {/* Rendering From In Save Case */}
            {!id && (
                <ManageAuthorForm
                    initialValues={initialValues}
                    submit={(values) => submit(values)}
                    id={id}
                    entitySearchListData={entitySearchListData}
                    locationData={locationData}
                    locationDataLoading={locationDataLoading}
                    userData={userData}
                    userPermission={userPermission}
                />
            )}
        </>
    );
};

export default ManageAuthor